import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Download } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import * as XLSX from "xlsx";
import { collection, getDocs } from "firebase/firestore";
import MenuBar from "../../components/MenuBar";
import HeaderBar from "../../components/HeaderBar";
import { db } from "../../firebase";
import { ATTENDANCE_BRANCHES } from "../../config/attendanceBranches";
import { getVisibleAttendanceBranches } from "../../config/appBranches";
import {
  loadMonthlyAttendanceRows,
  monthTitle,
  cutoffDateForSelectedMonth,
  buildHolidayYmdSetFromDocs,
  buildEmployeeCashLeaveYmdByEmployee,
  employeeCashLeaveYmdSet,
  getWorkingDayYmdsFullMonth,
  getWorkingDaysTillCutoff,
  formatPresentDayUnits,
  ymd,
} from "../../helpers/payrollAttendanceCore";
import {
  getDaysInMonth,
  buildDayCellsForRow,
  dayStatusLabel,
  DAY_STATUS,
  MonthAbsentCalendarLegend,
} from "../../components/payroll/MonthAbsentCalendar";

const ALL_BRANCHES = "__all__";

function getStoredUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
}

export default function PayrollMonthlyLeaveReport() {
  const storedUser = useMemo(() => getStoredUser(), []);
  const visibleBranches = useMemo(() => getVisibleAttendanceBranches(storedUser), [storedUser]);
  const branchPool = visibleBranches.length ? visibleBranches : ATTENDANCE_BRANCHES;

  const [month, setMonth] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [shopLabel, setShopLabel] = useState(ALL_BRANCHES);
  const [mode, setMode] = useState("cutoff");
  const [onlyWithAbsent, setOnlyWithAbsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [loadedOnce, setLoadedOnce] = useState(false);

  const daysInMonth = useMemo(() => getDaysInMonth(month), [month]);
  const showBranchCol = shopLabel === ALL_BRANCHES;
  const fixedColCount = (showBranchCol ? 1 : 0) + 4;
  const colWidths = useMemo(() => {
    const branch = showBranchCol ? 8 : 0;
    const emp = showBranchCol ? 32 : 36;
    const dept = 6;
    const pres = 4;
    const abs = 4;
    const fixed = branch + emp + dept + pres + abs;
    const dayPct = (100 - fixed) / Math.max(daysInMonth.length, 1);
    return { branch, emp, dept, pres, abs, dayPct };
  }, [showBranchCol, daysInMonth.length]);

  useEffect(() => {
    const allowed = new Set([ALL_BRANCHES, ...branchPool.map((b) => b.label)]);
    if (!allowed.has(shopLabel) && branchPool[0]) {
      setShopLabel(ALL_BRANCHES);
    }
  }, [branchPool, shopLabel]);

  const shiftMonth = (delta) => setMonth((m) => new Date(m.getFullYear(), m.getMonth() + delta, 1));

  const load = useCallback(async () => {
    setLoading(true);
    setRows([]);
    try {
      const holidaySnap = await getDocs(collection(db, "holidays")).catch(() => ({ docs: [] }));
      const monthStart = new Date(month.getFullYear(), month.getMonth(), 1);
      const cutoffDt = cutoffDateForSelectedMonth(month);
      const holidaySetCutoff = buildHolidayYmdSetFromDocs(holidaySnap.docs, monthStart, cutoffDt);
      const holidaySetFull = buildHolidayYmdSetFromDocs(
        holidaySnap.docs,
        monthStart,
        new Date(month.getFullYear(), month.getMonth() + 1, 0)
      );
      const workingList =
        mode === "fullMonth"
          ? getWorkingDayYmdsFullMonth(month, holidaySetFull)
          : getWorkingDaysTillCutoff(month, holidaySetCutoff);

      const windowEnd =
        mode === "fullMonth"
          ? new Date(month.getFullYear(), month.getMonth() + 1, 0)
          : cutoffDt;
      const cashLeaveByEmp = buildEmployeeCashLeaveYmdByEmployee(holidaySnap.docs, monthStart, windowEnd);
      const cutoffYmd = mode === "cutoff" ? ymd(cutoffDt) : null;

      const branchesToLoad =
        shopLabel === ALL_BRANCHES ? branchPool : branchPool.filter((b) => b.label === shopLabel);

      const merged = [];
      for (const br of branchesToLoad) {
        const base = await loadMonthlyAttendanceRows(br, month);
        for (const r of base.rows) {
          const punched = new Set([...(r.presentDates || []), ...(r.halfDayDates || [])]);
          const cashLeaveSet = employeeCashLeaveYmdSet(cashLeaveByEmp, r.employeeId);
          const expectedForEmp = workingList.filter((d) => !cashLeaveSet.has(d));
          const absentDates = expectedForEmp.filter((d) => !punched.has(d));
          const leaveDates = [...cashLeaveSet].filter((d) => {
            const parts = d.split("-").map(Number);
            return parts[0] === month.getFullYear() && parts[1] === month.getMonth() + 1;
          });
          const rowData = {
            branchLabel: br.label,
            employeeId: r.employeeId,
            employeeName: r.employeeName,
            department: r.department || "",
            presentDays: r.presentDays,
            absentDays: absentDates.length,
            absentDates,
            presentDates: r.presentDates || [],
            halfDayDates: r.halfDayDates || [],
            leaveDates,
          };
          merged.push({
            ...rowData,
            dayCells: buildDayCellsForRow(month, rowData, cutoffYmd),
          });
        }
      }
      merged.sort((a, b) => {
        const br = a.branchLabel.localeCompare(b.branchLabel);
        if (br !== 0) return br;
        return a.employeeName.localeCompare(b.employeeName, undefined, { sensitivity: "base" });
      });

      setRows(merged);
      setLoadedOnce(true);
      const withAbsent = merged.filter((r) => r.absentDays > 0).length;
      if (!merged.length) {
        toast.error("No employees found for selected branch(es)");
      } else {
        toast.success(
          `${monthTitle(month)}: ${merged.length} staff · ${withAbsent} with absent days (no punch)`
        );
      }
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Failed to load monthly absent report");
    } finally {
      setLoading(false);
    }
  }, [branchPool, month, mode, shopLabel]);

  useEffect(() => {
    load();
  }, [load]);

  const displayRows = useMemo(() => {
    if (!onlyWithAbsent) return rows;
    return rows.filter((r) => r.absentDays > 0);
  }, [rows, onlyWithAbsent]);

  const exportXlsx = () => {
    if (!displayRows.length) {
      toast.error("Nothing to export");
      return;
    }
    const data = displayRows.map((r) => {
      const o = {
        Branch: r.branchLabel,
        Employee: r.employeeName,
        "Employee ID": r.employeeId,
        Department: r.department,
        Present: formatPresentDayUnits(r.presentDays),
        "Absent days": r.absentDays,
      };
      for (const { day, status } of r.dayCells) {
        o[String(day)] = dayStatusLabel(status);
      }
      return o;
    });
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, "MonthlyAbsent");
    const br =
      shopLabel === ALL_BRANCHES ? "all-branches" : String(shopLabel).replace(/\s+/g, "-");
    const ym = `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, "0")}`;
    XLSX.writeFile(wb, `payroll-monthly-absent-${ym}-${br}.xlsx`);
    toast.success("Download started");
  };

  const emptyMessage = loadedOnce
    ? onlyWithAbsent
      ? "No absent days this month for any staff. Uncheck “Only staff with absent” to see everyone."
      : "No employees found."
    : "Loading absent report…";

  return (
    <div className="min-h-screen bg-slate-100/90">
      <Toaster position="top-right" />
      <MenuBar />
      <HeaderBar />
      <main className="mx-auto w-full max-w-[100vw] px-2 py-3 sm:px-3 sm:py-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-2 py-1 shadow-sm">
            <button type="button" onClick={() => shiftMonth(-1)} className="rounded-lg p-1 hover:bg-slate-100">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <span className="min-w-[10rem] text-center text-sm font-semibold">
              {month.toLocaleString("en-IN", { month: "long", year: "numeric" })}
            </span>
            <button type="button" onClick={() => shiftMonth(1)} className="rounded-lg p-1 hover:bg-slate-100">
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
          <label className="text-xs font-semibold text-slate-700">
            Branch
            <select
              value={shopLabel}
              onChange={(e) => setShopLabel(e.target.value)}
              className="mt-1 block min-w-[10rem] rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
            >
              <option value={ALL_BRANCHES}>All branches</option>
              {branchPool.map((b) => (
                <option key={b.label} value={b.label}>
                  {b.label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs font-semibold text-slate-700">
            Period
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              className="mt-1 block rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
            >
              <option value="cutoff">Month to date (through today)</option>
              <option value="fullMonth">Full calendar month</option>
            </select>
          </label>
          <label className="flex items-center gap-2 text-xs font-semibold text-slate-700">
            <input
              type="checkbox"
              checked={onlyWithAbsent}
              onChange={(e) => setOnlyWithAbsent(e.target.checked)}
              className="rounded border-slate-300"
            />
            Only staff with absent
          </label>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white shadow hover:bg-indigo-700 disabled:opacity-50"
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
          <button
            type="button"
            onClick={exportXlsx}
            disabled={!displayRows.length}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-800 shadow-sm hover:bg-slate-50 disabled:opacity-50"
          >
            <Download className="h-4 w-4" aria-hidden />
            Export
          </button>
        </div>

        <MonthAbsentCalendarLegend className="mt-3" />

        <div className="mt-4 w-full overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full table-fixed border-collapse text-xs leading-snug">
            <colgroup>
              {showBranchCol && <col style={{ width: `${colWidths.branch}%` }} />}
              <col style={{ width: `${colWidths.emp}%` }} />
              <col style={{ width: `${colWidths.dept}%` }} />
              <col style={{ width: `${colWidths.pres}%` }} />
              <col style={{ width: `${colWidths.abs}%` }} />
              {daysInMonth.map((d) => (
                <col key={d} style={{ width: `${colWidths.dayPct}%` }} />
              ))}
            </colgroup>
            <thead>
              <tr className="bg-slate-800 text-[11px] font-bold text-slate-100">
                {showBranchCol && (
                  <th className="border-r border-slate-600 px-2 py-2 text-left truncate">Br</th>
                )}
                <th className="border-r border-slate-600 px-2 py-2 text-left">Employee</th>
                <th className="border-r border-slate-600 px-1 py-2 text-left">D</th>
                <th className="border-r border-slate-600 px-1 py-2 text-right">P</th>
                <th className="border-r border-slate-700 px-1 py-2 text-right">A</th>
                {daysInMonth.map((d) => (
                  <th
                    key={d}
                    className="border-r border-slate-600/60 px-0 py-2 text-center text-[10px] font-bold tabular-nums last:border-r-0"
                  >
                    {d}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={fixedColCount + daysInMonth.length}
                    className="px-4 py-8 text-center text-slate-500"
                  >
                    {loading ? "Loading…" : emptyMessage}
                  </td>
                </tr>
              ) : (
                displayRows.map((r) => (
                  <tr
                    key={`${r.branchLabel}-${r.employeeId}`}
                    className={`border-t border-slate-100 ${r.absentDays > 0 ? "bg-orange-50/30" : "odd:bg-white even:bg-slate-50/50"}`}
                  >
                    {showBranchCol && (
                      <td
                        className="border-r border-slate-100 px-2 py-1.5 text-slate-600 truncate"
                        title={r.branchLabel}
                      >
                        {r.branchLabel}
                      </td>
                    )}
                    <td
                      className="border-r border-slate-100 px-2 py-1.5 text-slate-900"
                      title={`${r.employeeName} (${r.employeeId})`}
                    >
                      <span className="block truncate text-sm font-semibold leading-tight">{r.employeeName}</span>
                      <span className="mt-0.5 block truncate font-mono text-[10px] font-normal text-slate-500">
                        {r.employeeId}
                      </span>
                    </td>
                    <td
                      className="border-r border-slate-100 px-1 py-1.5 text-slate-600 truncate"
                      title={r.department}
                    >
                      {r.department || "—"}
                    </td>
                    <td className="border-r border-slate-100 px-1 py-1.5 text-right tabular-nums">
                      {formatPresentDayUnits(r.presentDays)}
                    </td>
                    <td className="border-r border-slate-200 px-1 py-1.5 text-right tabular-nums font-semibold text-orange-800">
                      {r.absentDays}
                    </td>
                    {r.dayCells.map(({ day, ymd: dayKey, status }) => (
                      <td key={day} className="border-r border-slate-100 p-0.5 last:border-r-0">
                        <div
                          className={`h-4 w-full min-h-[1rem] rounded-sm ${DAY_STATUS[status] || DAY_STATUS.empty}`}
                          title={`${dayKey} · ${dayStatusLabel(status)}`}
                        />
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
